import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../api';
import { useSuppliers } from '../queries';
import { Badge, Button, Card, ErrorMessage, Field, Input, Notice, Select } from '../ui';

export const STATUS_TONE = { good: 'green', damaged: 'red', quarantine: 'amber', in_transit: 'blue' } as const;
export const STATUS_WORD = { good: 'Good', damaged: 'Damaged', quarantine: 'Quarantine', in_transit: 'In transit' } as const;

/** Mark some or all of the pieces in a stock entry good, damaged or quarantined. */
export function StatusForm({ entry, onClose, onDone }: { entry: { id: number; qtyRemaining: number; status: string }; onClose: () => void; onDone: () => void }) {
  const [status, setStatus] = useState<'good' | 'damaged' | 'quarantine'>(entry.status === 'good' ? 'damaged' : 'good');
  const [qty, setQty] = useState(String(entry.qtyRemaining));
  const [note, setNote] = useState('');
  const run = useMutation({
    mutationFn: () => api(`/api/v1/stock-entries/${entry.id}/status`, { body: { status, qty: Number(qty), note: note || null } }),
    onSuccess: onDone,
  });
  return (
    <form
      className="mt-4 flex flex-wrap items-end gap-3 rounded-md bg-slate-50 p-3"
      onSubmit={(e) => {
        e.preventDefault();
        run.mutate();
      }}
    >
      <Field label="Mark as">
        <Select value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
          <option value="good">Good (available)</option>
          <option value="damaged">Damaged</option>
          <option value="quarantine">Quarantine</option>
        </Select>
      </Field>
      <Field label={`How many (of ${entry.qtyRemaining})`}>
        <Input inputMode="numeric" className="w-24" value={qty} onChange={(e) => setQty(e.target.value)} />
      </Field>
      <Field label="Note">
        <Input value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
      <Button type="submit" disabled={run.isPending || !(Number(qty) >= 1 && Number(qty) <= entry.qtyRemaining)}>
        Apply
      </Button>
      <Button variant="ghost" onClick={onClose}>
        Cancel
      </Button>
      <p className="basis-full text-xs text-slate-500">Only good stock counts as available: damaged and quarantined pieces stay on hand and costed, but are not sold, built or sent to BrickLink.</p>
      <div className="basis-full">
        <ErrorMessage error={run.error} />
      </div>
    </form>
  );
}

/** Set what the pieces still in a stock entry cost, with a reason. Past sales keep the cost they had. */
export function CostForm({ entry, onClose, onDone }: { entry: { id: number; unitCost: string; qtyRemaining: number }; onClose: () => void; onDone: () => void }) {
  const [cost, setCost] = useState(Number(entry.unitCost) > 0 ? String(Number(entry.unitCost)) : '');
  const [reason, setReason] = useState('');
  const run = useMutation({
    mutationFn: () => api(`/api/v1/stock-entries/${entry.id}/cost`, { body: { unitCost: cost, reason: reason || null } }),
    onSuccess: onDone,
  });
  return (
    <form
      className="mt-4 flex flex-wrap items-end gap-3 rounded-md bg-slate-50 p-3"
      onSubmit={(e) => {
        e.preventDefault();
        run.mutate();
      }}
    >
      <Field label="Cost of each piece">
        <Input inputMode="decimal" autoFocus className="w-28" placeholder="0.05" value={cost} onChange={(e) => setCost(e.target.value)} />
      </Field>
      <Field label="Why">
        <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. found the receipt" />
      </Field>
      <Button type="submit" disabled={run.isPending || cost.trim() === ''}>
        Set cost
      </Button>
      <Button variant="ghost" onClick={onClose}>
        Cancel
      </Button>
      <p className="basis-full text-xs text-slate-500">Changes the cost of the {entry.qtyRemaining} pieces in stock. What past sales cost is not changed. The change is recorded with who made it and why.</p>
      <div className="basis-full">
        <ErrorMessage error={run.error} />
      </div>
    </form>
  );
}

/** When to buy more of this lot: reorder below a level, order up to another, from a chosen supplier. */
export function ReorderCard({ itemId, colorId, condition }: { itemId: number; colorId: number; condition: string }) {
  const qc = useQueryClient();
  const suppliers = useSuppliers();
  const q = useQuery({
    queryKey: ['reorder-rule', itemId, colorId, condition],
    queryFn: () => api<{ rule: { minQty: number; targetQty: number; supplierId: number | null } | null }>(`/api/v1/reorder-rule?itemId=${itemId}&colorId=${colorId}&condition=${condition}`).then((r) => r.rule),
  });
  const [min, setMin] = useState('');
  const [target, setTarget] = useState('');
  const [sup, setSup] = useState('');
  useEffect(() => {
    if (q.data !== undefined) {
      setMin(q.data ? String(q.data.minQty) : '');
      setTarget(q.data ? String(q.data.targetQty) : '');
      setSup(q.data?.supplierId ? String(q.data.supplierId) : '');
    }
  }, [q.data]);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['reorder-rule', itemId, colorId, condition] });
    void qc.invalidateQueries({ queryKey: ['buy-list'] });
  };
  const save = useMutation({
    mutationFn: () => api('/api/v1/reorder-rule', { method: 'PUT', body: { itemId, colorId, condition, minQty: Number(min), targetQty: Number(target), supplierId: sup ? Number(sup) : null } }),
    onSuccess: refresh,
  });
  const remove = useMutation({ mutationFn: () => api(`/api/v1/reorder-rule?itemId=${itemId}&colorId=${colorId}&condition=${condition}`, { method: 'DELETE' }), onSuccess: refresh });
  const valid = Number.isInteger(Number(min)) && Number.isInteger(Number(target)) && min !== '' && target !== '' && Number(target) >= Number(min) && Number(min) >= 0;
  return (
    <Card title="Reorder level" className="lg:col-span-3">
      <div className="flex flex-wrap items-end gap-3 text-sm">
        <Field label="Reorder when below">
          <Input inputMode="numeric" className="w-24" value={min} onChange={(e) => setMin(e.target.value)} />
        </Field>
        <Field label="Order up to">
          <Input inputMode="numeric" className="w-24" value={target} onChange={(e) => setTarget(e.target.value)} />
        </Field>
        <Field label="Buy from">
          <Select value={sup} onChange={(e) => setSup(e.target.value)}>
            <option value="">Preferred supplier</option>
            {(suppliers.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
        </Field>
        <Button disabled={!valid || save.isPending} onClick={() => save.mutate()}>
          Save
        </Button>
        {q.data && (
          <Button variant="ghost" disabled={remove.isPending} onClick={() => remove.mutate()}>
            Remove
          </Button>
        )}
        {q.data && <Badge tone="blue">on the buy list when available plus on order is under {q.data.minQty}</Badge>}
      </div>
      <ErrorMessage error={save.error} />
      {!q.data && <Notice>No reorder level set. Below it, this lot appears on the Buy list with how many to buy.</Notice>}
    </Card>
  );
}
